import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Calendar, Clock, MapPin, User, ChevronLeft, ChevronRight, XCircle, Video } from 'lucide-react';
import { toast } from 'sonner';
import PortalLayout from './PortalLayout';
import api from '@/lib/axios';
import { getPortalToken } from './portalStorage';
import { formatDate, formatScheduleLabel } from '@/lib/utils';
import AbsenceReportDialog from './AbsenceReportDialog';

export default function PortalSchedule() {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [weekOffset, setWeekOffset] = useState(0);
  const [absenceDialog, setAbsenceDialog] = useState(null);

  useEffect(() => {
    fetchSchedule();
  }, [weekOffset]);

  const fetchSchedule = async () => {
    setLoading(true);
    try {
      const token = getPortalToken();
      if (token) {
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      }
      
      // Currently the API returns next 7 days from dashboard
      // For a full schedule view, we'd need a dedicated endpoint
      const response = await api.get('/portal/dashboard');
      setDashboard(response.data);
    } catch (error) {
      console.error('Error loading schedule:', error);
      toast.error('Fehler beim Laden des Stundenplans');
    } finally {
      setLoading(false);
    }
  };

  const getWeekDates = () => {
    const today = new Date();
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - today.getDay() + 1 + (weekOffset * 7)); // Monday
    
    const dates = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + i);
      dates.push(date);
    }
    return dates;
  };

  const weekDates = getWeekDates();
  const weekDays = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

  // Group lessons by date
  const lessonsByDate = {};
  (dashboard?.upcoming_lessons || []).forEach(lesson => {
    const dateKey = lesson.date;
    if (!lessonsByDate[dateKey]) {
      lessonsByDate[dateKey] = [];
    }
    lessonsByDate[dateKey].push(lesson);
  });

  return (
    <PortalLayout>
      <div className="space-y-6" data-testid="portal-schedule">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Stundenplan</h1>
            <p className="text-slate-600">Ihre Unterrichtstermine</p>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setWeekOffset(prev => prev - 1)}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setWeekOffset(0)}
            >
              Heute
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setWeekOffset(prev => prev + 1)}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Week Header */}
        <Card>
          <CardContent className="p-4">
            <div className="grid grid-cols-7 gap-2 text-center">
              {weekDates.map((date, idx) => {
                const isToday = date.toDateString() === new Date().toDateString();
                const dateStr = date.toISOString().split('T')[0];
                const hasLessons = lessonsByDate[dateStr]?.length > 0;
                
                return (
                  <div 
                    key={idx}
                    className={`p-2 rounded-lg ${
                      isToday ? 'bg-brand-red/10 text-brand-red' : ''
                    }`}
                  >
                    <p className="text-xs font-medium text-slate-500">{weekDays[idx]}</p>
                    <p className={`text-lg font-bold ${isToday ? 'text-brand-red' : 'text-slate-900'}`}>
                      {date.getDate()}
                    </p>
                    {hasLessons && (
                      <div className="w-2 h-2 bg-brand-red rounded-full mx-auto mt-1"></div>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Lessons */}
        {loading ? (
          <Card>
            <CardContent className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-red"></div>
            </CardContent>
          </Card>
        ) : (dashboard?.upcoming_lessons?.length || 0) === 0 ? (
          <Card>
            <CardContent className="p-8 text-center text-slate-500">
              Keine Termine in den nächsten 7 Tagen
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {dashboard?.upcoming_lessons?.map((lesson, idx) => (
              <Card key={idx}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <div className="text-center min-w-[60px] p-2 bg-brand-red/5 rounded-lg">
                      <p className="text-xs text-slate-500">{formatDate(lesson.date)}</p>
                      <p className="text-lg font-bold text-brand-red">{lesson.time}</p>
                    </div>
                    
                    <div className="flex-1">
                      <p className="font-medium text-slate-900">{formatScheduleLabel(lesson.course_name)}</p>
                      <div className="flex flex-wrap items-center gap-3 mt-1 text-sm text-slate-500">
                        {lesson.teacher_name && (
                          <span className="flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {lesson.teacher_name}
                          </span>
                        )}
                        {lesson.room_name && (
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {lesson.room_name}
                          </span>
                        )}
                        {lesson.is_online && (
                          <span className="flex items-center gap-1 text-emerald-600 font-medium" data-testid={`portal-online-badge-${lesson.lesson_event_id}`}>
                            <Video className="w-3 h-3" />
                            Online
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {lesson.duration_minutes} Min
                        </span>
                      </div>
                    </div>
                    
                    <Badge variant="outline">{lesson.student_name}</Badge>
                    {lesson.is_online && lesson.online_url && (
                      <Button
                        size="sm"
                        className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        onClick={() => window.open(lesson.online_url, '_blank', 'noopener')}
                        data-testid={`portal-join-online-${lesson.lesson_event_id}`}
                      >
                        <Video className="w-3 h-3 mr-1" /> Online teilnehmen
                      </Button>
                    )}
                    {lesson.lesson_event_id && lesson.student_id && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-rose-600 border-rose-200 hover:bg-rose-50"
                        onClick={() => setAbsenceDialog(lesson)}
                        data-testid={`portal-absence-btn-${lesson.lesson_event_id}`}
                      >
                        <XCircle className="w-3 h-3 mr-1" /> Abwesend
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
      {absenceDialog && (
        <AbsenceReportDialog
          lesson={absenceDialog}
          onClose={() => setAbsenceDialog(null)}
          onSubmitted={() => { setAbsenceDialog(null); fetchSchedule(); }}
        />
      )}
    </PortalLayout>
  );
}
